import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { ApiResponse } from '../types';
import { validateToken, sanitizeTokenForLogging } from '../utils/jwtUtils';

/**
 * API client for making requests to the backend
 */
export class ApiClient {
  private axiosInstance: AxiosInstance;
  private baseUrl: string;
  private isRefreshing = false;
  private refreshAttempts = 0;
  private maxRefreshAttempts = 3; // Allow more attempts but with better control
  private refreshPromise: Promise<string | null> | null = null;
  private refreshCooldown = 0;
  private consecutiveFailures = 0;
  private maxConsecutiveFailures = 3;
  private lastRefreshAttempt = 0;
  private minRefreshInterval = 5000; // 5 seconds minimum between refresh attempts
  private storedRefreshToken: string | null = null;
  private storedAuthToken: string | null = null;
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (error: any) => void;
    config: any;
  }> = [];

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      headers: {
        'Accept': 'application/json',
      },
      withCredentials: true, // Include credentials (cookies) in requests
    });

    // Add response interceptor for automatic token refresh
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Handle errors (e.g., unauthorized, server errors)
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          const responseData = error.response.data as any;
          console.error('API Error:', error.response.status, responseData?.message || 'Unknown error', 'URL:', originalRequest?.url || 'unknown');
          
          // Log validation errors without sensitive data
          if (responseData?.errors) {
            console.error('Validation errors found');
            Object.keys(responseData.errors).forEach((field) => {
              console.error(`Field validation error: ${field}`);
            });
          }

          // Handle 401 Unauthorized - attempt automatic token refresh
          if (error.response.status === 401 && !originalRequest._retry) {
            return this.handleTokenRefresh(originalRequest, error);
          }

          // Handle 403 Forbidden - user is authenticated but lacks permission
          if (error.response.status === 403) {
            // Check for subscription inactive response
            if (responseData?.subscriptionInactive) {
              console.warn('403 Forbidden - subscription inactive for:', originalRequest?.url);
              if (typeof localStorage !== 'undefined') {
                localStorage.setItem('subscriptionInactive', 'true');
              }
              if (typeof window !== 'undefined' && window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent('auth:subscriptionInactive', {
                  detail: { url: originalRequest?.url }
                }));
              }
            } else {
              console.warn('403 Forbidden - insufficient permissions for:', originalRequest?.url);
            }
            if (typeof window !== 'undefined' && window.dispatchEvent) {
              window.dispatchEvent(new CustomEvent('auth:forbidden', {
                detail: { url: originalRequest?.url }
              }));
            }
          }

          // Handle 502/503/504 - server temporarily unavailable (e.g. during deploys).
          // Retry with exponential backoff for safe requests.
          const serverUnavailable = [502, 503, 504].includes(error.response.status);
          if (serverUnavailable && originalRequest) {
            const retryCount = originalRequest._retryCount || 0;
            const isRetryable = originalRequest.method === 'get' ||
              originalRequest.url?.includes('/auth/refresh-token');

            if (isRetryable && retryCount < 3) {
              originalRequest._retryCount = retryCount + 1;
              const delay = Math.pow(2, retryCount) * 2000; // 2s, 4s, 8s
              console.log(`Server unavailable (${error.response.status}), retrying in ${delay / 1000}s... (attempt ${retryCount + 1}/3)`);
              await new Promise(resolve => setTimeout(resolve, delay));
              return this.axiosInstance(originalRequest);
            }
          }
        } else if (error.request) {
          // The request was made but no response was received
          console.error('Network Error: No response received');
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Request Error:', error.message);
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * Enhanced token refresh with request queueing and infinite loop prevention.
   * When a 401 triggers a refresh, subsequent 401s queue their requests and wait.
   * After the refresh completes, all queued requests are replayed with the new token.
   */
  private async handleTokenRefresh(originalRequest: any, error: AxiosError): Promise<any> {
    console.log('401 detected, attempting automatic token refresh...');

    // Don't attempt token refresh for auth endpoints — these return 401 for
    // invalid credentials, not expired tokens. Refreshing after a failed login
    // creates a pointless loop (POST /auth/refresh-token with no token → 400).
    if (originalRequest.url?.includes('/auth/')) {
      return Promise.reject(error);
    }

    // Check if we're in a cooldown period
    const now = Date.now();
    if (now - this.lastRefreshAttempt < this.minRefreshInterval) {
      console.log('Refresh cooldown active, rejecting request');
      this.clearStoredAuthData();
      this.handleAuthenticationFailure();
      return Promise.reject(error);
    }

    // Check consecutive failures
    if (this.consecutiveFailures >= this.maxConsecutiveFailures) {
      console.log('Max consecutive failures reached, logging out user');
      this.clearStoredAuthData();
      this.handleAuthenticationFailure();
      return Promise.reject(error);
    }

    // Check if we've already tried refreshing too many times in this session
    if (this.refreshAttempts >= this.maxRefreshAttempts) {
      console.log('Max refresh attempts reached, logging out user');
      this.clearStoredAuthData();
      this.handleAuthenticationFailure();
      return Promise.reject(error);
    }

    // If a refresh is already in progress, queue this request and wait
    if (this.refreshPromise) {
      console.log('Token refresh in progress, queueing request:', originalRequest.url);
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    // Mark request as retried and start the refresh
    originalRequest._retry = true;
    this.lastRefreshAttempt = now;
    this.refreshAttempts++;

    // Create and store the refresh promise
    this.refreshPromise = this.performTokenRefresh();

    try {
      const token = await this.refreshPromise;
      if (token) {
        // Reset consecutive failures on success
        this.consecutiveFailures = 0;

        // Replay all queued requests with the new token
        this.failedQueue.forEach(({ resolve, config }) => {
          config.headers['Authorization'] = 'Bearer ' + token;
          config._retry = true;
          resolve(this.axiosInstance(config));
        });
        this.failedQueue = [];

        // Retry the original request with the new token
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return this.axiosInstance(originalRequest);
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (refreshError) {
      this.consecutiveFailures++;
      console.error('Token refresh failed:', refreshError);

      // Reject all queued requests
      this.failedQueue.forEach(({ reject }) => reject(refreshError));
      this.failedQueue = [];

      // Clear stored authentication data
      this.clearStoredAuthData();

      // Redirect to login or trigger logout
      this.handleAuthenticationFailure();

      return Promise.reject(refreshError);
    } finally {
      // Clear the refresh promise
      this.refreshPromise = null;
    }
  }
  
  /**
   * Perform the actual token refresh operation
   */
  private async performTokenRefresh(): Promise<string | null> {
    try {
      console.log('🔄 Attempting token refresh...');

      // Bail early if we have no access token — the backend needs it to extract the
      // Cognito username. Without it the request will fail anyway.
      if (!this.storedAuthToken) {
        console.warn('No stored access token — cannot refresh, user must log in again');
        throw new Error('No access token available for refresh');
      }

      // The backend needs the (possibly expired) access token to extract the Cognito
      // username for the REFRESH_TOKEN_AUTH flow. Send it alongside the refresh token.
      // Web clients also rely on the HttpOnly cookie for the refresh token.
      const refreshResponse = await this.axiosInstance.post('/auth/refresh-token', {
        token: this.storedAuthToken,
        refreshToken: this.storedRefreshToken || undefined,
      });
      
      if (refreshResponse.data && refreshResponse.data.token) {
        const newToken = refreshResponse.data.token;
        console.log('✅ Token refresh successful, new token:', sanitizeTokenForLogging(newToken));
        
        // Reset refresh attempts on success
        this.refreshAttempts = 0;
        
        // Update the token in the API client
        this.setAuthToken(newToken);

        // Update stored refresh token if a rotated one was returned
        if (refreshResponse.data.refreshToken) {
          this.storedRefreshToken = refreshResponse.data.refreshToken;
        }

        // Update localStorage with new token and user data (only in web)
        if (typeof localStorage !== 'undefined') {
          if (refreshResponse.data.user) {
            localStorage.setItem('user', JSON.stringify(refreshResponse.data.user));
          }
          localStorage.setItem('token', newToken);
          localStorage.setItem('tokenExpiration', refreshResponse.data.expiration);
        }
        
        return newToken;
      } else {
        throw new Error('No token received from refresh endpoint');
      }
    } catch (refreshError) {
      console.error('❌ Token refresh failed:', refreshError);
      
      // Check if this is a refresh token cookie issue
      const axiosRefreshError = refreshError as any;
      const isRefreshTokenError = axiosRefreshError?.response?.data?.message?.includes('Refresh token is required') ||
                                axiosRefreshError?.response?.status === 401;
      
      if (isRefreshTokenError) {
        console.log('🍪 Refresh token cookie missing or invalid - user needs to log in again');
      }
      
      throw refreshError;
    }
  }

  /**
   * Clear stored authentication data
   */
  private clearStoredAuthData() {
    console.log('Clearing stored authentication data due to refresh failure');
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiration');
    }
    this.clearAuthToken(); // also clears storedAuthToken
    this.storedRefreshToken = null;
    // Reset refresh attempts when clearing data
    this.refreshAttempts = 0;
    // Don't reset consecutiveFailures — preserve it so the max-failures
    // safeguard still kicks in for rapid-fire refresh attempts.
    this.refreshPromise = null;
    // Reject and clear any queued requests
    this.failedQueue.forEach(({ reject }) => reject(new Error('Auth data cleared')));
    this.failedQueue = [];
  }

  /**
   * Handle authentication failure
   */
  private handleAuthenticationFailure() {
    console.log('🚪 Authentication failed, triggering logout...');
    
    // Dispatch a custom event that the AuthContext can listen to (only in web)
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('auth:logout', {
        detail: { reason: 'token_refresh_failed' }
      }));
    }
  }

  /**
   * Set the authentication token for all future requests
   */
  setAuthToken(token: string): void {
    // Validate token before setting it (but don't be too strict)
    const validation = validateToken(token);

    if (!validation.isValid) {
      console.warn('⚠️ Setting potentially invalid token in API client:', validation.reason);
      console.warn('🔑 Token being set anyway:', sanitizeTokenForLogging(token));
      // Still set the token - let the backend decide if it's valid
    } else {
      console.log('🔑 Setting valid authentication token:', sanitizeTokenForLogging(token));
    }

    this.storedAuthToken = token;
    this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Clear the authentication token
   */
  clearAuthToken(): void {
    console.log('🚫 Clearing authentication token');
    this.storedAuthToken = null;
    delete this.axiosInstance.defaults.headers.common['Authorization'];
  }

  /**
   * Get the stored access token (used to include in refresh requests so the backend
   * can extract the username for the Cognito refresh flow)
   */
  getAuthToken(): string | null {
    return this.storedAuthToken;
  }

  /**
   * Set the refresh token for mobile clients (stored in-memory, sent in request body)
   */
  setRefreshToken(token: string): void {
    this.storedRefreshToken = token;
  }

  /**
   * Get the base URL used by this API client
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Get the stored refresh token (used by authService for revocation)
   */
  getRefreshToken(): string | null {
    return this.storedRefreshToken;
  }

  /**
   * Clear the stored refresh token
   */
  clearRefreshToken(): void {
    this.storedRefreshToken = null;
  }

  /**
   * Reset refresh state (useful for testing or manual resets)
   */
  resetRefreshState(): void {
    console.log('🔄 Resetting refresh state');
    this.refreshAttempts = 0;
    this.consecutiveFailures = 0;
    this.refreshPromise = null;
    this.lastRefreshAttempt = 0;
  }

  /**
   * Make a GET request
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse = await this.axiosInstance.get(url, config);
      return {
        data: response.data,
        success: true,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Make a POST request
   */
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      // Verbose debug logs commented out for cleaner console output
      // Uncomment for API debugging if needed
      // console.log('🔍 API CLIENT DEBUG - POST Request Details:');
      // console.log('🔍 URL:', url);
      // console.log('🔍 Data type:', typeof data);
      
      // IMPROVED VALIDATION: Allow empty objects and undefined data for endpoints that don't require a body
      // Only validate serialization if data is actually provided
      let serializedData: string | undefined;
      if (data !== null && data !== undefined) {
        try {
          serializedData = JSON.stringify(data);
          // console.log('🔍 Serialized data length:', serializedData.length);
          
          // Only reject if serialization truly failed or produced null
          // Empty objects {} are VALID for action endpoints like /auth/revoke-token
          if (!serializedData || serializedData === 'null') {
            console.error('❌ Data serialization failed');
            return {
              success: false,
              message: 'Data serialization failed',
            };
          }
        } catch (serializationError) {
          console.error('❌ Serialization error:', serializationError);
          return {
            success: false,
            message: 'Failed to serialize request data',
          };
        }
      }
      // else: No data - silent, this is normal for some endpoints

      // CRITICAL FIX: Ensure Content-Type is set for JSON data and remove any conflicting headers
      const requestConfig: AxiosRequestConfig = {
        ...config,
        headers: {
          'Content-Type': 'application/json',
          ...config?.headers,
        },
        // Ensure we're sending the data as JSON
        transformRequest: [
          function (data, headers) {
            // Ensure Content-Type is application/json
            headers['Content-Type'] = 'application/json';
            // Return the already serialized JSON string
            return typeof data === 'string' ? data : JSON.stringify(data);
          }
        ],
      };

      // Verbose config logs commented out
      // console.log('🔍 Final request config headers:', requestConfig.headers);

      const response: AxiosResponse = await this.axiosInstance.post(url, data, requestConfig);
      
      // Success - silent (only log errors)
      return {
        data: response.data,
        success: true,
      };
    } catch (error) {
      console.error('❌ API CLIENT - POST request failed');
      return this.handleError(error);
    }
  }

  /**
   * Make a PUT request
   */
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      // Verbose debug logs commented out
      // console.log('🔍 API CLIENT DEBUG - PUT Request Details:');
      
      // IMPROVED VALIDATION: Allow empty objects and undefined data for endpoints that don't require a body
      // Only validate serialization if data is actually provided
      let serializedData: string | undefined;
      if (data !== null && data !== undefined) {
        try {
          serializedData = JSON.stringify(data);
          
          // Only reject if serialization truly failed or produced null
          // Empty objects {} are VALID for action endpoints
          if (!serializedData || serializedData === 'null') {
            console.error('❌ PUT data serialization failed');
            return {
              success: false,
              message: 'Data serialization failed',
            };
          }
        } catch (serializationError) {
          console.error('❌ PUT serialization error:', serializationError);
          return {
            success: false,
            message: 'Failed to serialize request data',
          };
        }
      }

      // CRITICAL FIX: Ensure Content-Type is set for JSON data and remove any conflicting headers
      const requestConfig: AxiosRequestConfig = {
        ...config,
        headers: {
          'Content-Type': 'application/json',
          ...config?.headers,
        },
        // Ensure we're sending the data as JSON
        transformRequest: [
          function (data, headers) {
            // Ensure Content-Type is application/json
            headers['Content-Type'] = 'application/json';
            // Return the already serialized JSON string
            return typeof data === 'string' ? data : JSON.stringify(data);
          }
        ],
      };

      const response: AxiosResponse = await this.axiosInstance.put(url, data, requestConfig);
      
      // Success - silent
      return {
        data: response.data,
        success: true,
      };
    } catch (error) {
      console.error('❌ API CLIENT - PUT request failed to:', url);
      return this.handleError(error);
    }
  }

  /**
   * Make a PATCH request
   */
  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      // Verbose debug logs commented out
      // console.log('🔍 API CLIENT DEBUG - PATCH Request Details:');
      
      // IMPROVED VALIDATION: Allow empty objects and undefined data for endpoints that don't require a body
      // Only validate serialization if data is actually provided
      let serializedData: string | undefined;
      if (data !== null && data !== undefined) {
        try {
          serializedData = JSON.stringify(data);
          
          // Only reject if serialization truly failed or produced null
          // Empty objects {} are VALID for action endpoints
          if (!serializedData || serializedData === 'null') {
            console.error('❌ PATCH data serialization failed');
            return {
              success: false,
              message: 'Data serialization failed',
            };
          }
        } catch (serializationError) {
          console.error('❌ PATCH serialization error:', serializationError);
          return {
            success: false,
            message: 'Failed to serialize request data',
          };
        }
      }

      // CRITICAL FIX: Ensure Content-Type is set for JSON data and remove any conflicting headers
      const requestConfig: AxiosRequestConfig = {
        ...config,
        headers: {
          'Content-Type': 'application/json',
          ...config?.headers,
        },
        // Ensure we're sending the data as JSON
        transformRequest: [
          function (data, headers) {
            // Ensure Content-Type is application/json
            headers['Content-Type'] = 'application/json';
            // Return the already serialized JSON string
            return typeof data === 'string' ? data : JSON.stringify(data);
          }
        ],
      };

      const response: AxiosResponse = await this.axiosInstance.patch(url, data, requestConfig);
      
      // Success - silent
      return {
        data: response.data,
        success: true,
      };
    } catch (error) {
      console.error('❌ API CLIENT - PATCH request failed to:', url);
      return this.handleError(error);
    }
  }

  /**
   * Make a DELETE request
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse = await this.axiosInstance.delete(url, config);
      return {
        data: response.data,
        success: true,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Handle API errors
   */
  private handleError(error: unknown): ApiResponse<any> {
    // Type assertion for error to handle TypeScript error
    const axiosError = error as { 
      response?: { 
        data: { message?: string; error?: string; errors?: any };
        status?: number;
      };
      request?: any;
      message?: string;
    };

    if (axiosError.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      
      // Handle 401 Unauthorized - token might be expired
      if (axiosError.response.status === 401) {
        console.warn('🚫 Unauthorized request - automatic refresh should have been attempted');
        // Don't automatically clear token here - the interceptor handles it
        return {
          success: false,
          message: 'Authentication required. Please log in again.',
          errors: { unauthorized: true, status: 401 },
        };
      }
      
      // Handle 413 Payload Too Large specifically
      if (axiosError.response.status === 413) {
        return {
          success: false,
          message: 'Request payload too large. Please reduce image sizes and try again.',
          errors: { payloadTooLarge: true, status: 413 },
        };
      }
      
      return {
        success: false,
        message: axiosError.response.data?.message || 'An error occurred',
        error: axiosError.response.data?.error,
        errors: axiosError.response.data?.errors,
      };
    } else if (axiosError.request) {
      // The request was made but no response was received
      return {
        success: false,
        message: 'Network error. Please check your connection.',
      };
    } else {
      // Something happened in setting up the request that triggered an Error
      return {
        success: false,
        message: axiosError.message || 'An unexpected error occurred',
      };
    }
  }
}

// Create a default instance
// Use window._env_ for environment variables in browser context
let API_URL = typeof window !== 'undefined' && window._env_ && window._env_.REACT_APP_API_URL
  ? window._env_.REACT_APP_API_URL
  : (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_API_URL)
    ? process.env.EXPO_PUBLIC_API_URL
    : (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
      ? process.env.REACT_APP_API_URL
      : 'https://d3t71xc0hyi6iy.cloudfront.net/api';

// Check if we're in production
const isProduction = typeof window !== 'undefined' && 
  window.location &&
  (!window.location.hostname.includes('localhost') && 
   !window.location.hostname.includes('127.0.0.1'));

// We're allowing HTTP for the Elastic Beanstalk backend
// Uncomment the below code if you want to force HTTPS in the future
/*
if (isProduction && !API_URL.startsWith('https://') && !API_URL.includes('localhost')) {
  API_URL = API_URL.replace('http://', 'https://');
}
*/

console.log('🌐 API Client initialized with URL:', API_URL);
export const apiClient = new ApiClient(API_URL);

// Add type definition for window._env_
declare global {
  interface Window {
    _env_?: {
      REACT_APP_API_URL?: string;
      [key: string]: any;
    };
  }
}

export default apiClient;
